import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  BookOpenCheck,
  BrainCircuit,
  Clock3,
  FilePlus2,
  Layers3,
  Plus,
  ShieldCheck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import SEO from "@/components/site/SEO";
import lmsApi from "@/lib/lmsApi";

function slugify(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function LMSCourseAuthoring() {
  const navigate = useNavigate();

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [slugEdited, setSlugEdited] = useState(false);

  const [form, setForm] = useState({
    title: "",
    slug: "",
    description: "",
    estimated_minutes: "",
    cpd_minutes: "",
    pass_mark: "",
  });

  const load = async () => {
    setError("");

    try {
      const me = await lmsApi.get("/auth/me");

      if (me.data.require_password_change) {
        navigate("/learn/change-password", {
          replace: true,
        });
        return;
      }

      if (!me.data.is_platform_admin) {
        navigate("/learn", {
          replace: true,
        });
        return;
      }

      const response = await lmsApi.get("/author/courses");

      setCourses(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/learn/login", {
          replace: true,
        });
        return;
      }

      setError(
        err.response?.data?.detail ||
          "We could not load the course catalogue."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const counts = useMemo(
    () => ({
      total: courses.length,

      published: courses.filter(
        (course) => course.status === "published"
      ).length,

      draft: courses.filter(
        (course) =>
          course.latest_draft_version !== null
      ).length,
    }),
    [courses]
  );

  const handleTitleChange = (value) => {
    setForm((current) => ({
      ...current,
      title: value,
      slug: slugEdited
        ? current.slug
        : slugify(value),
    }));
  };

  const handleCreate = async (event) => {
    event.preventDefault();

    setSubmitting(true);
    setError("");

    try {
      const response = await lmsApi.post(
        "/author/courses",
        {
          title: form.title,
          slug: form.slug,

          description:
            form.description || null,

          estimated_minutes:
            form.estimated_minutes
              ? Number(form.estimated_minutes)
              : null,

          cpd_minutes:
            form.cpd_minutes
              ? Number(form.cpd_minutes)
              : null,

          pass_mark:
            form.pass_mark
              ? Number(form.pass_mark)
              : null,
        }
      );

      navigate(
        `/learn/admin/courses/${response.data.id}`
      );
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "The course could not be created."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <section className="min-h-screen bg-slate-50 py-20">
        <div className="container-rail text-center text-slate-600">
          Loading course authoring…
        </div>
      </section>
    );
  }

  return (
    <>
      <SEO
        title="Course Authoring | TRAC-Learn"
        description="Secure UKTDA course authoring and publishing."
        path="/learn/admin/courses"
      />

      <section className="bg-navy py-10 text-white md:py-14">
        <div className="container-rail">
          <button
            type="button"
            onClick={() =>
              navigate("/learn/admin")
            }
            className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Platform administration
          </button>

          <div className="mt-6 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <div className="flex items-center gap-2 text-safety">
                <ShieldCheck className="h-5 w-5" />

                <p className="label-eyebrow">
                  UKTDA Super Admin
                </p>
              </div>

              <h1 className="mt-3 font-display text-4xl font-bold md:text-5xl">
                Course authoring
              </h1>

              <p className="mt-3 max-w-2xl text-white/70">
                Build draft learning, preview it, publish an immutable version,
                then grant that course to selected organisations.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() =>
                  navigate(
                    "/learn/admin/assessments"
                  )
                }
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/20 bg-white/5 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10"
              >
                <BrainCircuit className="h-4 w-4" />
                Assessments
              </button>

              <button
                type="button"
                onClick={() =>
                  setShowCreate(
                    (value) => !value
                  )
                }
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-safety px-5 py-3 text-sm font-bold text-white"
              >
                <Plus className="h-4 w-4" />
                New course
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="min-h-[60vh] bg-slate-50 py-10 md:py-14">
        <div className="container-rail">
          {error && (
            <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              {error}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-3">
            {[
              [
                BookOpenCheck,
                "Courses",
                counts.total,
              ],
              [
                Layers3,
                "Published",
                counts.published,
              ],
              [
                FilePlus2,
                "Drafts",
                counts.draft,
              ],
            ].map(([Icon, label, value]) => (
              <div
                key={label}
                className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <div className="flex items-center gap-3 text-sm font-semibold text-slate-500">
                  <Icon className="h-5 w-5 text-safety" />
                  {label}
                </div>

                <p className="mt-3 font-display text-3xl font-bold text-navy">
                  {value}
                </p>
              </div>
            ))}
          </div>

          {showCreate && (
            <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
              <p className="label-eyebrow text-safety">
                New course
              </p>

              <h2 className="mt-2 font-display text-3xl font-bold text-navy">
                Create the course shell
              </h2>

              <p className="mt-3 max-w-3xl text-slate-600">
                TRAC-Learn creates version 1 as a draft. Nothing becomes
                available to organisations or learners until that version is
                published and access is granted.
              </p>

              <form
                onSubmit={handleCreate}
                className="mt-7 grid gap-5 md:grid-cols-2"
              >
                <label className="text-sm font-semibold text-navy md:col-span-2">
                  Course title

                  <input
                    value={form.title}
                    onChange={(event) =>
                      handleTitleChange(
                        event.target.value
                      )
                    }
                    required
                    className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3 outline-none focus:border-safety focus:ring-2 focus:ring-safety/20"
                    placeholder="Rail Careers Programme"
                  />
                </label>

                <label className="text-sm font-semibold text-navy md:col-span-2">
                  Description

                  <textarea
                    value={form.description}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        description:
                          event.target.value,
                      }))
                    }
                    rows={4}
                    className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3 outline-none focus:border-safety"
                  />
                </label>

                <label className="text-sm font-semibold text-navy">
                  Course slug

                  <input
                    value={form.slug}
                    onChange={(event) => {
                      setSlugEdited(true);

                      setForm((current) => ({
                        ...current,
                        slug: slugify(
                          event.target.value
                        ),
                      }));
                    }}
                    required
                    className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3 font-mono text-sm outline-none focus:border-safety"
                  />
                </label>

                <label className="text-sm font-semibold text-navy">
                  Estimated learning time (minutes)

                  <input
                    type="number"
                    min="1"
                    value={
                      form.estimated_minutes
                    }
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        estimated_minutes:
                          event.target.value,
                      }))
                    }
                    className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3 outline-none focus:border-safety"
                  />
                </label>

                <label className="text-sm font-semibold text-navy">
                  CPD minutes

                  <input
                    type="number"
                    min="0"
                    value={form.cpd_minutes}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        cpd_minutes:
                          event.target.value,
                      }))
                    }
                    className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3 outline-none focus:border-safety"
                  />
                </label>

                <label className="text-sm font-semibold text-navy">
                  Default pass mark (%)

                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={form.pass_mark}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        pass_mark:
                          event.target.value,
                      }))
                    }
                    className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3 outline-none focus:border-safety"
                  />
                </label>

                <div className="flex gap-3 md:col-span-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="rounded-lg bg-navy px-5 py-3 text-sm font-bold text-white disabled:opacity-60"
                  >
                    {submitting
                      ? "Creating…"
                      : "Create draft course"}
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setShowCreate(false)
                    }
                    className="rounded-lg border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="mt-10 flex items-end justify-between gap-4">
            <div>
              <p className="label-eyebrow text-safety">
                Catalogue
              </p>

              <h2 className="mt-2 font-display text-3xl font-bold text-navy">
                UKTDA courses
              </h2>
            </div>
          </div>

          {courses.length ? (
            <div className="mt-6 grid gap-5 lg:grid-cols-2">
              {courses.map((course) => (
                <button
                  key={course.id}
                  type="button"
                  onClick={() =>
                    navigate(
                      `/learn/admin/courses/${course.id}`
                    )
                  }
                  className="rounded-2xl border border-slate-200 bg-white p-6 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-mono text-xs text-slate-400">
                        {course.slug}
                      </p>

                      <h3 className="mt-2 font-display text-2xl font-bold text-navy">
                        {course.title}
                      </h3>
                    </div>

                    <span
                      className={`rounded-full px-3 py-1 text-xs font-bold ${
                        course.status ===
                        "published"
                          ? "bg-emerald-50 text-emerald-700"
                          : course.status ===
                            "archived"
                          ? "bg-slate-100 text-slate-600"
                          : "bg-amber-50 text-amber-700"
                      }`}
                    >
                      {course.status}
                    </span>
                  </div>

                  {course.description && (
                    <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-slate-600">
                      {course.description}
                    </p>
                  )}

                  <div className="mt-5 flex flex-wrap gap-4 text-xs text-slate-500">
                    <span>
                      Versions:{" "}
                      {course.version_count}
                    </span>

                    <span>
                      Published:{" "}
                      {course.latest_published_version ??
                        "—"}
                    </span>

                    <span>
                      Draft:{" "}
                      {course.latest_draft_version ??
                        "—"}
                    </span>

                    <span className="inline-flex items-center gap-1">
                      <Clock3 className="h-3.5 w-3.5" />
                      Updated{" "}
                      {new Date(
                        course.updated_at
                      ).toLocaleDateString(
                        "en-GB"
                      )}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
              <BookOpenCheck className="mx-auto h-10 w-10 text-slate-400" />

              <h3 className="mt-4 font-display text-xl font-bold text-navy">
                No courses yet
              </h3>

              <p className="mt-2 text-slate-600">
                Create the first course to start building the UKTDA learning
                catalogue.
              </p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}